import type { AutomaticAlignmentController } from "./hooks/useAutomaticAttachmentAlignment";
import { ManualAttachmentExecutionGate } from "./AutomaticAttachmentAlignmentPanel";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FolderTree, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { AppBindingResponse } from "@/types/api";
import {
  useAsynchronousExecutionCommand,
  useAsynchronousExecutions,
  useOrphanRecordFolderCleanupPlanPreview,
  useStartOrphanRecordFolderCleanupExecution,
  type OrphanRecordFolderCleanupPlanPreviewResponse,
} from "./hooks/useAppBindings";
import { ActiveExecutionPanel } from "./ActiveExecutionPanel";

interface OrphanRecordFolderCleanupSectionProps {
  binding: AppBindingResponse;
  automaticAlignment: AutomaticAlignmentController;
}

export function OrphanRecordFolderCleanupSection({
  binding,
  automaticAlignment,
}: OrphanRecordFolderCleanupSectionProps) {
  const [preview, setPreview] =
    useState<OrphanRecordFolderCleanupPlanPreviewResponse | null>(null);
  const [operatorConfirmed, setOperatorConfirmed] = useState(false);
  const startRequest = useRef<{ previewSnapshotId: string; confirmedAt: string } | null>(null);
  useEffect(() => {
    if (!preview) return;
    const timer = setTimeout(() => setOperatorConfirmed(false), Math.max(0, Date.parse(preview.confirmExpiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [preview?.confirmExpiresAt]);
  const previewPlan = useOrphanRecordFolderCleanupPlanPreview(
    binding.kintoneAppId,
  );
  const startExecution = useStartOrphanRecordFolderCleanupExecution(
    binding.kintoneAppId,
  );
  const executionHistory = useAsynchronousExecutions({
    kintoneAppId: binding.kintoneAppId,
    activeOnly: "true",
    limit: "1",
  });
  const activeExecution = executionHistory.items.find(
    (item) => item.appBindingId === binding.appBindingId &&
      ["queued", "running", "blocked", "cancel_requested"].includes(item.executionState),
  );
  const cancelExecution = useAsynchronousExecutionCommand(
    binding.kintoneAppId,
    activeExecution?.executionKind ?? "orphan_record_folder_cleanup",
    activeExecution?.executionId ?? "",
    "cancel",
  );
  const resumeExecution = useAsynchronousExecutionCommand(
    binding.kintoneAppId,
    activeExecution?.executionKind ?? "orphan_record_folder_cleanup",
    activeExecution?.executionId ?? "",
    "resume",
  );
  const canStart = Boolean(
    automaticAlignment.canStartManual &&
      !executionHistory.isLoading && !executionHistory.error && !activeExecution && !previewPlan.isLoading &&
      preview &&
      preview.summary.candidateCount > 0 &&
      preview.summary.blockedCount === 0 &&
      Date.parse(preview.confirmExpiresAt) > Date.now() &&
      operatorConfirmed,
  );
  const error = previewPlan.error ?? startExecution.error;

  if (binding.modeId !== "attachment_compatible") return null;

  async function handlePreview() {
    startRequest.current = null;
    try {
      setPreview(await previewPlan.mutate({}));
      setOperatorConfirmed(false);
    } catch {
      setPreview(null);
      setOperatorConfirmed(false);
    }
  }

  async function handleStart() {
    if (!preview || !canStart) return;
    try {
      if (startRequest.current?.previewSnapshotId !== preview.previewSnapshotId)
        startRequest.current = { previewSnapshotId: preview.previewSnapshotId, confirmedAt: new Date().toISOString() };
      await startExecution.mutate(startRequest.current);
      setPreview(null);
      setOperatorConfirmed(false);
    } catch { /* The start hook retains the failure; keep the confirmation input. */ }
    finally {
      await executionHistory.refresh();
      await automaticAlignment.refresh();
    }
  }

  return (
    <section className="space-y-4 pt-2" aria-labelledby="orphan-folder-cleanup-heading">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="orphan-folder-cleanup-heading" className="text-lg font-semibold">
            孤立レコードフォルダ整理
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            kintone に存在しないレコード番号の外部フォルダを確認し、管理下の隔離領域へ移動します。
          </p>
        </div>
        <Button asChild type="button" variant="outline" size="sm">
          <Link to={`/console/asynchronous-executions?executionKind=orphan_record_folder_cleanup&kintoneAppId=${encodeURIComponent(binding.kintoneAppId)}`}>
            実行履歴を見る
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">整理対象を確認して開始</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {activeExecution ? (
            <ActiveExecutionPanel kintoneAppId={binding.kintoneAppId}
              execution={activeExecution}
              busy={resumeExecution.isLoading || cancelExecution.isLoading}
              stateVerified={!executionHistory.isLoading && !executionHistory.error}
              stateReadFailed={Boolean(executionHistory.error)}
              onRefresh={executionHistory.refresh}
              onResume={() => resumeExecution.mutate({ expectedRevision: activeExecution.revision, expectedExecutionState: "blocked", blockerResolutionConfirmed: true, reason: "管理者が阻害要因の解消を確認" }).then(() => executionHistory.refresh())}
              onCancel={() => cancelExecution.mutate({ expectedRevision: activeExecution.revision, expectedExecutionState: activeExecution.executionState, reason: "管理者が取消を要求" }).then(() => executionHistory.refresh())}
            />
          ) : null}
          <ManualAttachmentExecutionGate controller={automaticAlignment} />
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" onClick={() => void handlePreview()} disabled={previewPlan.isLoading}>
              {previewPlan.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FolderTree className="mr-2 h-4 w-4" />}
              候補を確認
            </Button>
            {preview && preview.summary.candidateCount > 0 ? (
              <Button type="button" onClick={() => void handleStart()} disabled={!canStart || startExecution.isLoading}>
                {startExecution.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                整理を開始
              </Button>
            ) : null}
          </div>

          {error ?? executionHistory.error ?? cancelExecution.error ?? resumeExecution.error ? (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <span>{error ?? executionHistory.error ?? cancelExecution.error ?? resumeExecution.error}</span>
            </div>
          ) : null}

          {preview && preview.summary.candidateCount === 0 && preview.summary.blockedCount === 0 ? (
            <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              整理が必要なフォルダはありません。
            </p>
          ) : preview ? (
            <div className="space-y-4 rounded-lg border border-border p-4">
              <div className="grid gap-3 text-sm md:grid-cols-4">
                <SummaryCell label="走査" value={`${preview.summary.scannedRecordFolderCount} 件`} />
                <SummaryCell label="候補" value={`${preview.summary.candidateCount} / ${preview.summary.scannedRecordFolderCount} フォルダ`} />
                <SummaryCell label="既存レコード" value={`${preview.summary.existingRecordFolderCount} 件`} />
                <SummaryCell label="要確認" value={`${preview.summary.blockedCount} 件`} />
              </div>
              <p className="text-sm">アプリ {preview.kintoneAppId} ／ Storage {preview.storageId} ／ 基点 {preview.appBaseFolder}<br />
                確認期限: {new Date(preview.confirmExpiresAt).toLocaleString()}。探索したレコードIDは {preview.summary.scannedRecordIdCount} 件です。</p>
              {preview.summary.candidateCount === preview.summary.scannedRecordFolderCount && preview.summary.candidateCount > 0 ?
                <p role="alert" className="text-sm text-amber-950">全フォルダが候補です。アプリとストレージの対応が正しいか確認してください。</p> : null}
              {preview.summary.blockedCount > 0 ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                  確認できない候補があります。権限、kintone API トークン、ストレージの応答を確認してください。
                </div>
              ) : null}
              <label className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm">
                <input type="checkbox" className="mt-1 h-4 w-4" checked={operatorConfirmed} disabled={preview.summary.blockedCount > 0} onChange={(event) => setOperatorConfirmed(event.target.checked)} />
                <span>候補を確認し、対象フォルダを管理下の隔離領域へ移動することを承認します。</span>
              </label>
              <ul className="space-y-1 text-sm" aria-label="孤立フォルダ候補">
                {preview.candidates.map(candidate => <li key={candidate.recordFolderEntryRef}>{candidate.recordFolderEntryRef}（レコード {candidate.recordId}）</li>)}
              </ul>
              {preview.candidateCursor ? <Button variant="outline" type="button" disabled={previewPlan.isLoading} onClick={() => {
                void previewPlan.mutate({ previewSnapshotId: preview.previewSnapshotId, candidateCursor: preview.candidateCursor! }).then(setPreview).catch(() => setOperatorConfirmed(false));
              }}>次の候補を表示</Button> : null}
              <ul className="space-y-1 text-sm" aria-label="確認できないフォルダ">
                {preview.blockers.map((blocker, index) => <li key={index}>{blocker.recordFolderEntryRef ?? blocker.recordId}：{blocker.reasonCode}</li>)}
              </ul>
              {preview.blockerCursor ? <Button variant="outline" type="button" disabled={previewPlan.isLoading} onClick={() => {
                void previewPlan.mutate({ previewSnapshotId: preview.previewSnapshotId, blockerCursor: preview.blockerCursor! }).then(setPreview).catch(() => setOperatorConfirmed(false));
              }}>次の要確認項目を表示</Button> : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              まず［候補を確認］を押してください。確認だけではフォルダは移動しません。
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
